import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Box, Chip, Divider, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useCRUD } from "../../../../api/crud";
import { EQUIPMENT_ATTRIBUTES_ENDPOINT } from "../../../../consts";
import { basicInfoContainerStyle } from "../../../../styles/commonStyles";
import { useBreadcrumbs } from "../../../../components/ui/breadcrumps/breadcrumbsContext";
import CustomCircularProgress from "../../../../components/ui/customCircularProgress/customCircularProgress.component";

interface AttributeChoice {
  id: number;
  name: string;
}

interface EquipmentAttribute {
  id: number;
  name: string;
  choices?: AttributeChoice[];
}

export const ConfigurationBasicInfo = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const { get } = useCRUD(EQUIPMENT_ATTRIBUTES_ENDPOINT);
  const { setOverride } = useBreadcrumbs();
  const [attribute, setAttribute] = useState<EquipmentAttribute | null>(null);

  useEffect(() => {
    const fetchConfiguration = async () => {
      if (!id) return;
      const response = await get(id);
      if (!response?.data) return;
      setAttribute(response.data);
      setOverride(
        `/equipment-section/configurations/${id}`,
        response.data.name || id
      );
    };

    const handleUpdated = () => fetchConfiguration();
    window.addEventListener("equipmentAttributeUpdated", handleUpdated);
    fetchConfiguration();
    return () => {
      window.removeEventListener("equipmentAttributeUpdated", handleUpdated);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (!attribute) return <CustomCircularProgress />;

  const choices = Array.isArray(attribute.choices) ? attribute.choices : [];

  return (
    <Box sx={basicInfoContainerStyle}>
      <Box>
        <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>
          {attribute.name}
        </Typography>
      </Box>

      <Divider />

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {t("Basic Information")}
        </Typography>
        <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, p: 2 }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
            {t("Name")}
          </Typography>
          <Typography variant="body1">{attribute.name || "—"}</Typography>
        </Box>
      </Box>

      <Divider />

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {t("Choices")}
        </Typography>
        {choices.length > 0 ? (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {choices.map((choice) => (
              <Chip key={choice.id} label={choice.name} size="small" variant="outlined" />
            ))}
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary" fontStyle="italic">
            {t("No choices")}
          </Typography>
        )}
      </Box>
    </Box>
  );
};
